import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout, { DateInput } from './Layout';
import { formatDate, getApiUrl, getTodayIso } from './config';

function Voucher() {
    const [suppliers, setSuppliers] = useState([]);
    const [voucher, setVoucher] = useState({ supplier_id: '', date: getTodayIso(), note: '' });
    const [payments, setPayments] = useState([{ payment_mode: 'Cash', amount: 0 }]);
    const [prevBalance, setPrevBalance] = useState(0);
    const [editingId, setEditingId] = useState(null);
    const [editingOriginalSupplierId, setEditingOriginalSupplierId] = useState(null);
    const [editingOriginalTotal, setEditingOriginalTotal] = useState(0);
    const [from, setFrom] = useState(getTodayIso());
    const [to, setTo] = useState(getTodayIso());
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchSuppliers();
    }, []);

    useEffect(() => {
        if (!voucher.supplier_id) {
            setPrevBalance(0);
            return;
        }
        const fetchBalance = async () => {
            try {
                const res = await axios.get(`${getApiUrl()}/suppliers/${voucher.supplier_id}/balance`);
                setPrevBalance(Number(res.data.balance) || 0);
            } catch (err) {
                console.error(err);
                setPrevBalance(0);
            }
        };
        fetchBalance();
    }, [voucher.supplier_id]);

    const fetchSuppliers = async () => {
        try {
            const res = await axios.get(`${getApiUrl()}/suppliers`);
            setSuppliers(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchVouchers = async () => {
        try {
            setLoading(true);
            const params = {};
            if (from) params.from = from;
            if (to) params.to = to;
            const res = await axios.get(`${getApiUrl()}/vouchers`, { params });
            setRows(res.data || []);
        } catch (err) {
            console.error(err);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVouchers();
    }, [from, to]);

    const resetForm = () => {
        setVoucher({ supplier_id: '', date: getTodayIso(), note: '' });
        setPayments([{ payment_mode: 'Cash', amount: 0 }]);
        setPrevBalance(0);
        setEditingId(null);
        setEditingOriginalSupplierId(null);
        setEditingOriginalTotal(0);
    };

    const handleVoucher = async (e) => {
        e.preventDefault();
        try {
            const normalizedPayments = payments
                .map(p => ({ payment_mode: p.payment_mode, amount: parseFloat(p.amount) || 0 }))
                .filter(p => p.payment_mode && p.amount !== 0);
            if (editingId) {
                await axios.put(`${getApiUrl()}/vouchers/${editingId}`, { ...voucher, payments: normalizedPayments });
                alert('Voucher Updated');
            } else {
                await axios.post(`${getApiUrl()}/vouchers`, { ...voucher, payments: normalizedPayments });
                alert('Voucher Recorded');
            }
            resetForm();
            fetchVouchers();
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.code ||
                "Failed to save voucher";
            alert(message);
        }
    };

    const handleEdit = async (id) => {
        try {
            const res = await axios.get(`${getApiUrl()}/vouchers/${id}`);
            const data = res.data || {};
            const invoiceDate = String(data.date || "").slice(0, 10) || getTodayIso();
            setEditingId(id);
            setVoucher({
                supplier_id: String(data.supplier_id || ""),
                date: invoiceDate,
                note: data.note || ""
            });
            setEditingOriginalSupplierId(String(data.supplier_id || ""));
            const nextPayments = Array.isArray(data.payments) && data.payments.length > 0
                ? data.payments.map(p => ({ payment_mode: p.payment_mode || "Cash", amount: Number(p.amount) || 0 }))
                : [{ payment_mode: "Cash", amount: Number(data.amount) || 0 }];
            setPayments(nextPayments);
            const originalTotal = nextPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
            setEditingOriginalTotal(originalTotal);
        } catch (err) {
            console.error(err);
            alert("Failed to load voucher");
        }
    };

    const handleDelete = async (id) => {
        const ok = window.confirm("Delete this voucher?");
        if (!ok) return;
        try {
            await axios.delete(`${getApiUrl()}/vouchers/${id}`);
            if (String(editingId) === String(id)) resetForm();
            fetchVouchers();
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.code ||
                "Failed to delete voucher";
            alert(message);
        }
    };

    return (
        <Layout>
            <div className="container mt-4">
                <h2>Payment Voucher</h2>
                {(() => {
                    const totalEntered = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                    const prevForForm = (editingId && String(voucher.supplier_id) === String(editingOriginalSupplierId))
                        ? ((Number(prevBalance) || 0) + (Number(editingOriginalTotal) || 0))
                        : (Number(prevBalance) || 0);
                    const balanceAfterForm = prevForForm - totalEntered;
                    return (
                <form onSubmit={handleVoucher} className="card p-4">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Supplier</label>
                            <select className="form-select" value={voucher.supplier_id} onChange={e => setVoucher({...voucher, supplier_id: e.target.value})} required>
                                <option value="">Select Supplier</option>
                                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Date</label>
                            <DateInput value={voucher.date} onChange={(v) => setVoucher({ ...voucher, date: v })} required />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Total</label>
                            <input
                                type="number"
                                className="form-control"
                                value={totalEntered}
                                readOnly
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Previous Balance</label>
                            <input type="number" className="form-control" value={prevForForm} readOnly />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Remaining Amount</label>
                            <input
                                type="number"
                                className="form-control"
                                value={balanceAfterForm}
                                readOnly
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Note</label>
                            <textarea className="form-control" value={voucher.note} onChange={e => setVoucher({...voucher, note: e.target.value})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Payments</label>
                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>Mode</th>
                                            <th>Amount</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map((p, idx) => (
                                            <tr key={idx}>
                                                <td>
                                                    <select
                                                        className="form-select"
                                                        value={p.payment_mode}
                                                        onChange={e => {
                                                            const next = [...payments];
                                                            next[idx] = { ...next[idx], payment_mode: e.target.value };
                                                            setPayments(next);
                                                        }}
                                                    >
                                                        <option value="Cash">Cash</option>
                                                        <option value="UPI">UPI</option>
                                                        <option value="Bank Transfer">Bank Transfer</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={p.amount}
                                                        onChange={e => {
                                                            const next = [...payments];
                                                            next[idx] = { ...next[idx], amount: e.target.value };
                                                            setPayments(next);
                                                        }}
                                                        onFocus={(e) => e.target.select()}
                                                    />
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => setPayments(payments.filter((_, i) => i !== idx))}
                                                        disabled={payments.length === 1}
                                                    >
                                                        Remove
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setPayments([...payments, { payment_mode: 'Cash', amount: 0 }])}
                            >
                                Add Mode
                            </button>
                        </div>
                        <div className="col-12 mt-3 d-grid d-md-block">
                            <button type="submit" className="btn btn-warning btn-lg">
                                {editingId ? "Update Voucher" : "Save Voucher"}
                            </button>
                            {editingId ? (
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-lg ms-2"
                                    onClick={resetForm}
                                >
                                    Cancel Edit
                                </button>
                            ) : null}
                        </div>
                    </div>
                </form>
                    );
                })()}

                <div className="card p-3 mt-4">
                    <div className="d-flex flex-wrap align-items-end gap-3">
                        <div>
                            <label className="form-label">From</label>
                            <DateInput value={from} onChange={setFrom} />
                        </div>
                        <div>
                            <label className="form-label">To</label>
                            <DateInput value={to} onChange={setTo} />
                        </div>
                        <div className="ms-auto">
                            <button type="button" className="btn btn-outline-primary" onClick={fetchVouchers} disabled={loading}>
                                {loading ? "Loading..." : "Refresh"}
                            </button>
                        </div>
                    </div>

                    <div className="table-responsive mt-3">
                        <table className="table table-bordered">
                            <thead className="table-dark">
                                <tr>
                                    <th>Date</th>
                                    <th>Supplier</th>
                                    <th>Total</th>
                                    <th>Mode</th>
                                    <th>Note</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r.id}>
                                        <td>{formatDate(r.date)}</td>
                                        <td>{r.supplier_name || "-"}</td>
                                        <td>{Number(r.amount) || 0}</td>
                                        <td>{r.payment_mode || "-"}</td>
                                        <td style={{ minWidth: "200px" }}>{r.note || "-"}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleEdit(r.id)}>
                                                Edit
                                            </button>
                                            <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={() => handleDelete(r.id)}>
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {(!rows || rows.length === 0) ? (
                                    <tr>
                                        <td colSpan="6" className="text-center">No vouchers</td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default Voucher;
